import * as types from './mutation-type'
import { API_STATES } from "@/helpers";
import { pick, cloneDeep, findIndex, isEmpty } from 'lodash'
import storage from '@/modules/storage'
import CryptoJSAES from 'crypto-js/aes'
import UTF8 from 'crypto-js/enc-utf8'
import router from '@/router'
import { uuid } from 'vue-uuid'

const decryptData = conversation => {
  if(conversation.body) {
    const bytes  = CryptoJSAES.decrypt(conversation.body, process.env.VUE_APP_MESSAGE_ENCRYPTION_SECRET)
    
    return { ...conversation, body : bytes.toString(UTF8) }
  }

  return {
    ...conversation,
    Messages       : conversation.Messages.map(decryptData),
    UnreadMessages : conversation.UnreadMessages.map(decryptData)
  }
}

const getUsersInfo = (state, participants)  => {
  const attributes = ['id', 'fullname', 'photo']

  if(participants.length > 1) {
    attributes.push('firstname')
  }
  
  return participants.map((pId, index) => {
    if(state.mappedUsersOnId[pId]) {
      return pick(state.mappedUsersOnId[pId], attributes)
    }

    return { id : `Inactive/Deleted_Employee_${index}`, fullname : 'Inactive/Deleted Employee' }
  })
}

const mutations = {
  [types.SHOW_RECIPIENT_FIELD](state, isShow){
    state.showSelectRecipient = isShow
    state.newMessage = {
      text       : 'New Message',
      background : 'new-message'
    }
    state.showNewAnnouncements=false
  },
  [types.SHOW_ANNOUNCEMENT_FIELD](state, { isShow, isStore }){
    state.showNewAnnouncements = isShow
    state.newMessage = {
      text       : 'New Announcement',
      background : isStore ? 'store-announcement' : 'new-announcement'
    }
    state.showSelectRecipient=false
  },
  [types.SHOW_NEW_MESSAGE](state, isShow){
    state.showNewMessage = isShow
    state.activeRoomId = null
    state.countState=0
  },

  [types.UPDATE_MESSAGE_STATUS_READ_SUCCESS](state, conversation){
    conversation.Messages =  conversation.Messages.concat(conversation.UnreadMessages)
   
    state.countState = conversation.UnreadMessages.length 
    conversation.UnreadMessages = []
    state[conversation.type === 'user' ? 'conversations' : 'announcements'][state.activeRoomId] = conversation
  },

  [types.CREATE_CHAT_BEGIN](state) {
    state.chatCreateState = API_STATES.REQUEST_PENDING
  },
  [types.CREATE_CHAT_SUCCESS](state) {
    state.showNewMessage = false
    state.chatCreateError = null;
    state.chatCreateState = API_STATES.REQUEST_SUCCESS
    if(state.announcements[state.activeRoomId] || state.conversations[state.activeRoomId]){
      state.countState=0
    }
  },
  [types.CREATE_CHAT_ERROR](state, { err, data }) {
    state.chatCreateState = API_STATES.REQUEST_ERROR;
    state.chatCreateError = err;

    if (!data.chatRoomId) {
      return
    }

    const failedMessages = cloneDeep(state.failedMessages)

    if (!failedMessages[data.chatRoomId]) {
      failedMessages[data.chatRoomId] = new Array()
    }

    failedMessages[data.chatRoomId].push(decryptData({
      id         : uuid.v4(),
      chatRoomId : data.chatRoomId,
      body       : data.message,
      senderId   : this.state.app.User.id,
      createdAt  : new Date(),
      updatedAt  : new Date(),
      failed     : true,
      type       : data.type
    }))

    state.failedMessages = failedMessages
  },
  [types.GET_STORE_EMPLOYEES_BEGIN](state) {
    state.getStoreEmployeesState = API_STATES.REQUEST_PENDING
  },
  [types.GET_STORE_EMPLOYEES_SUCCESS](state, data) {
    state.userStoreEmployees = data.data.company.employeesOfStores
    state.getStoreEmployeesError = null;
    state.getStoreEmployeesState = API_STATES.REQUEST_SUCCESS
  },
  [types.GET_STORE_EMPLOYEES_ERROR](state, err) {
    state.getStoreEmployeesState = API_STATES.REQUEST_ERROR;
    state.getStoreEmployeesError = err;
  },
  [types.GET_CONVERSATIONS_BEGIN](state) {
    state.conversationsGetState = API_STATES.REQUEST_PENDING
  },
  [types.GET_CONVERSATIONS_SUCCESS](state, { data, rootState, dispatch }) {
    const conversations = {}
    const announcements = {}

    rootState.app.companyEmployees.forEach(e => {
      state.mappedUsersOnId[e.id] = e
    })
    
    const defaultData = {
      Messages       : [],
      UnreadMessages : [],
      Participants   : []
    }

    data.forEach(conversation => {
      dispatch("getChatRoomMessages", conversation)
      
      if(conversation.type === 'user') {
        conversations[conversation.id] = {
          ...conversation,
          ...defaultData
        }
      } else {
        announcements[conversation.id] = {
          ...conversation,
          ...defaultData
        }
      }
    })
    
    state.conversations = conversations
    state.announcements = announcements

    state.conversationsGetError = null;
    state.conversationsGetState = API_STATES.REQUEST_SUCCESS

    if (storage.get('notificationSettings')) {
      state.notificationSettings = storage.get('notificationSettings');
    }

    const activeRoomId = storage.get('activeChatRoomId') || ''
    
    if (conversations[activeRoomId] || announcements[activeRoomId]) {
      state.activeRoomId = activeRoomId
    } else if (!isEmpty(conversations)) {
      state.activeRoomId = Object.keys(conversations)[0];
    } else if (!isEmpty(announcements)) {
      state.activeRoomId = Object.keys(announcements)[0];
    } else {
      state.activeRoomId = ''
    }

    if (state.activeRoomId !== '') {
      state.showNewMessage = false 
    }
  },  
  [types.GET_CONVERSATIONS_ERROR](state, err) {
    state.conversationsGetState = API_STATES.REQUEST_ERROR;
    state.conversationsGetError = err;
  },
  [types.GET_CHATROOM_MESSAGES_BEGIN](state, chatRoomId) {
    state.getChatRoomMessagesState = { ...state.getChatRoomMessagesState, [chatRoomId] : true }
  },
  [types.GET_CHATROOM_MESSAGES_SUCCESS](state, { chatRoom, data }) {
    const chatRoomId = (data.UnreadMessages[0] || data.Messages[0] || { chatRoomId : chatRoom.id }).chatRoomId
    
    state.getChatRoomMessagesState = { ...state.getChatRoomMessagesState, [chatRoomId] : false }

    data = decryptData(data)

    if(state.conversations[chatRoomId]) {
      state.conversations[chatRoomId] = {
        ...state.conversations[chatRoomId],
        ...data,
        Participants : getUsersInfo(state, data.Participants)
      }
    }else {
      state.announcements[chatRoomId] = {
        ...state.announcements[chatRoomId],
        ...data
      }
    }
  },
  [types.GET_CHATROOM_MESSAGES_ERROR](state, { chatRoom }) {
    state.getChatRoomMessagesState = { ...state.getChatRoomMessagesState, [chatRoom.id] : false }
  },
  [types.SOCKET_MESSAGE_RECEIVED](state, messageData) {
    const { Participants, Message: encryptedMessage, ChatRoom } = messageData

    if(router.history.current.path === '/chat') {
      let conversation = null
      const messageType = ChatRoom.type === 'user' ? 'conversations' : 'announcements'
      let conversations = cloneDeep(state[messageType])
        
      const message = decryptData(encryptedMessage)
        
      if(!state.activeRoomId) {
        state.activeRoomId = message.chatRoomId
        storage.set('activeChatRoomId',message.chatRoomId);
      }

      if(state.activeRoomId===message.chatRoomId){
        state.countState+=1
      }

      const currentUserId = this.state.app.User.id

      const amISender = currentUserId === message.senderId
      const hasSeen = !message.MessageStatuses.length || message.MessageStatuses.find(ms => ms.userId === currentUserId && ms.seen)
        
      if((conversation = conversations[message.chatRoomId])){
        delete conversations[message.chatRoomId]
        conversations = Object.assign({ 
          [message.chatRoomId] : { 
            ...conversation, 
            ...(state.activeRoomId === message.chatRoomId || amISender || hasSeen ?  
              { Messages : [...conversation.Messages, message] } :
              { UnreadMessages : [...conversation.UnreadMessages, message] })
          } 
        }, conversations)
      }else {
        conversations = Object.assign({ 
          [message.chatRoomId] : { 
            ...ChatRoom,
            id             : message.chatRoomId,
            UnreadMessages : (amISender || hasSeen) ? [] : [message], 
            Messages       : (amISender || hasSeen) ? [message] : [],
            Participants   : ChatRoom.type === 'user' ? getUsersInfo(state, Participants): Participants
          } 
        }, conversations)
      }
      state[messageType] = conversations
    }
  },
  [types.SOCKET_ADD_NEW_CHAT_ROOM](state, data) {
    state.announcements = Object.assign(state.announcements, {
      [data.id] : decryptData(data)
    })
  },
  [types.SET_CHAT_ROOM_ID](state, chatRoomId) {
    state.activeRoomId = chatRoomId
    storage.set('activeChatRoomId',chatRoomId);
  },
  [types.NEW_MESSAGE_TOGGLE](state) {
    state.newMessageToggle = !state.newMessageToggle
  },
  [types.JOIN_CHAT_ROOM_BEGIN](state) {
    state.joinChatRoomState = API_STATES.REQUEST_PENDING
  },
  [types.JOIN_CHAT_ROOM_SUCCESS](state) {
    state.joinChatRoomState = API_STATES.REQUEST_SUCCESS
    state.joinChatRoomError = null
  },
  [types.JOIN_CHAT_ROOM_ERROR](state, e) {
    state.joinChatRoomState = API_STATES.REQUEST_ERROR
    state.joinChatRoomError = e
  },
  
  [types.GET_UNREAD_MESSAGE_LENGTH_SUCCESS](state, data) {
    state.showRedDot = data
  },

  [types.LOAD_MORE_MESSAGES_BEGIN](state) {
    state.loadMessagesState = API_STATES.REQUEST_PENDING
  },
  [types.LOAD_MORE_MESSAGES_SUCCESS](state, data) {
    state.loadMessagesState = API_STATES.REQUEST_SUCCESS
    if (state.announcements[state.activeRoomId] && state.announcements[state.activeRoomId].type) {
      state.announcements[state.activeRoomId].Messages.unshift(...(data.reverse().map(decryptData)));
    } else {
      state.conversations[state.activeRoomId].Messages.unshift(...(data.reverse().map(decryptData)));
    }
  },
  [types.LOAD_MORE_MESSAGES_ERROR](state, e) {
    state.loadMessagesState = API_STATES.REQUEST_ERROR
    state.loadMessagesError = e
  },
  [types.RETRY_FAILED_MESSAGES_BEGIN](state) {
    state.retryFailedMessageState = API_STATES.REQUEST_PENDING
  },
  [types.RETRY_FAILED_MESSAGES_SUCCESS](state, variables) {
    const index = findIndex(state.failedMessages[variables.chatRoomId], function(o) {
      return o.id === variables.id;
    });
    state.failedMessages[variables.chatRoomId].splice(index, 1)
    state.retryFailedMessageState = API_STATES.REQUEST_SUCCESS
    state.retryFailedMessageError = null
  },
  [types.RETRY_FAILED_MESSAGES_ERROR](state, err) {
    state.retryFailedMessageState = API_STATES.REQUEST_ERROR
    state.retryFailedMessageError = err
  },
  [types.SET_LAST_MESSAGE_RECEIVED_ID](state, messageId) {
    state.lastMessageReceivedId = messageId
  },
  [types.UPDATE_NOTIFICATION_OPTIONS](state,  data) {
    const notificationSettings = cloneDeep(state.notificationSettings)

    if (!notificationSettings[data.type]) {
      notificationSettings[data.type] = {}
    }

    notificationSettings[data.type] = Object.assign(notificationSettings[data.type], {
      [data.id] : {
        sms   : data.sms,
        email : data.email
      }
    })

    state.notificationSettings = notificationSettings
    storage.set('notificationSettings',notificationSettings);
  },
  [types.DELETE_CHAT_HISTORY_BEGIN](state) {
    state.deleteChatHistoryState = API_STATES.REQUEST_PENDING
  }, 
  [types.DELETE_CHAT_HISTORY_SUCCESS](state, chatRoomId) {
    const conversations = cloneDeep(state.conversations)
    delete conversations[chatRoomId]

    state.conversations = conversations
    if (Object.keys(conversations).length > 0) {
      state.activeRoomId = Object.keys(conversations)[0];
    } else if (Object.keys(state.announcements).length > 0) {
      state.activeRoomId = Object.keys(state.announcements)[0];
    } else {
      state.activeRoomId = ''
    }

    state.deleteChatHistoryState = API_STATES.REQUEST_SUCCESS
    state.deleteChatHistoryError = null
  },
  [types.DELETE_CHAT_HISTORY_ERROR](state, err) {
    state.deleteChatHistoryState = API_STATES.REQUEST_ERROR
    state.deleteChatHistoryError = err
  },
  [types.INTERNET_CONNECTED](state, { dispatch }) {
    if (!Object.keys(state.conversations).length && !Object.keys(state.announcements).length) {
      dispatch("getConversations")
    } else {
      Object.values(state.conversations).forEach(conversation => {
        if (!conversation.UnreadMessages.length && !conversation.Messages.length) {
          dispatch("getChatRoomMessages", conversation)
        }
      });
  
      Object.values(state.announcements).forEach(announcement => {
        if (!announcement.UnreadMessages.length && !announcement.Messages.length) {
          dispatch("getChatRoomMessages", announcement)
  
        }
      });
    }
  }
}

export default mutations